import { BrazeBottomSheetProps } from 'features/home/HomeScreen/components/BrazeBottomSheet/BrazeBottomSheet';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  BrazeContentCard,
  BrazeSuccessProductOfferCard,
  BrazeSuccessProductOfferCardIDs,
} from 'services/engagement/braze/types';
import {
  getExpoPushToken,
  logContentCardClicked,
  logContentCardDismissed,
  logContentCardImpression,
  logCustomEvent,
  subscribeToContentCardsUpdates,
} from 'services/engagement/engagementManager';
import { ProductOfferCardProps } from 'shared/components/vxl/cards/ProductOfferCard/ProductOfferCard';

// ============================================================================
// Context Types
// ============================================================================

interface EngagementContextProps {
  brazeCards: BrazeContentCard[] | undefined;
  marketingCarouselCards: BrazeContentCard[] | undefined;
  bottomSheetContent: BrazeBottomSheetProps | null;
  clearBottomSheetContent: () => void;
  expoPushToken: string | null;
  setRequestedCardId: (id: BrazeSuccessProductOfferCardIDs | null) => void;
  requestedCard: ProductOfferCardProps | undefined;
  // Braze actions - proxied from manager
  logContentCardClicked: (
    card:
      | BrazeContentCard
      | BrazeSuccessProductOfferCard
      | BrazeBottomSheetProps,
  ) => void;
  logContentCardDismissed: (
    card: BrazeContentCard | BrazeBottomSheetProps,
  ) => void;
  logContentCardImpression: (
    card:
      | BrazeContentCard
      | BrazeSuccessProductOfferCard
      | BrazeBottomSheetProps,
  ) => void;
  logCustomEvent: (
    eventName: string,
    eventProperties?: Record<string, unknown>,
  ) => void;
  removeBrazeCard: (cardId: string) => void;
}

export const EngagementContext = createContext<EngagementContextProps>(
  {} as EngagementContextProps,
);

// ============================================================================
// Provider Component - Owns all engagement state
// Subscribes to Braze content cards and provides state via context
// Login effects (updateUser, syncPushToken) are handled elsewhere
// ============================================================================

export const EngagementProvider = ({ children }: PropsWithChildren) => {
  // Content cards state
  const [brazeCards, setBrazeCards] = useState<BrazeContentCard[] | undefined>(
    undefined,
  );
  const [marketingCarouselCards, setMarketingCarouselCards] = useState<
    BrazeContentCard[] | undefined
  >(undefined);
  const [bottomSheetContent, setBottomSheetContent] =
    useState<BrazeBottomSheetProps | null>(null);
  const [productOfferCards, setProductOfferCards] = useState<{
    [key: string]: BrazeSuccessProductOfferCard;
  }>({});

  // Requested card state
  const [requestedCardId, setRequestedCardId] =
    useState<BrazeSuccessProductOfferCardIDs | null>(null);

  // Push token from manager (set during initialization)
  const [expoPushToken, setExpoPushToken] = useState<string | null>(
    getExpoPushToken,
  );

  // Remove card from state when dismissed
  const removeBrazeCard = useCallback((cardId: string) => {
    setBrazeCards((prev) => prev?.filter((card) => card.id !== cardId));
  }, []);

  // Compute requested card from productOfferCards and requestedCardId
  const requestedCard = useMemo((): ProductOfferCardProps | undefined => {
    if (!requestedCardId || !productOfferCards[requestedCardId]) {
      return undefined;
    }

    const foundCard = productOfferCards[requestedCardId];
    // Log impression when card is requested
    logContentCardImpression(foundCard);

    return {
      ...foundCard,
      onPress: () => {
        logContentCardClicked(foundCard);
      },
    };
  }, [requestedCardId, productOfferCards]);

  // Subscribe to content card updates from Braze
  useEffect(() => {
    subscribeToContentCardsUpdates(
      (cards, bottomSheet, newProductOfferCards, marketingCarouselCards) => {
        setBrazeCards(cards);
        setMarketingCarouselCards(marketingCarouselCards);
        setBottomSheetContent(bottomSheet);
        setProductOfferCards((prev) => ({ ...prev, ...newProductOfferCards }));
      },
    );
  }, []);

  // Sync push token from manager (in case it was set during init before mount)
  useEffect(() => {
    const token = getExpoPushToken();
    if (token && token !== expoPushToken) {
      setExpoPushToken(token);
    }
  }, [expoPushToken]);

  const clearBottomSheetContent = useCallback(() => {
    setBottomSheetContent(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      brazeCards,
      setBrazeCards,
      marketingCarouselCards,
      bottomSheetContent,
      clearBottomSheetContent,
      expoPushToken,
      setRequestedCardId,
      requestedCard,
      logContentCardClicked,
      logContentCardDismissed,
      logContentCardImpression,
      logCustomEvent,
      removeBrazeCard,
    }),
    [
      brazeCards,
      marketingCarouselCards,
      bottomSheetContent,
      clearBottomSheetContent,
      expoPushToken,
      requestedCard,
      removeBrazeCard,
    ],
  );

  return (
    <EngagementContext.Provider value={contextValue}>
      {children}
    </EngagementContext.Provider>
  );
};
